// @workflow tasks.show-in-3d
/**
 * Every viewer call the task actions make, in one small seam over the viewer adapter
 * (`viewerFor(store)`, src/platform/viewer). Nothing in src/actions reaches forgeService or the
 * Forge viewer itself; each operation forwards to the adapter's, which forwards to today's
 * legacy call unchanged.
 */
import { viewerFor } from '@/platform/viewer';

/** @param {import('vuex').Store} store */
export function createTaskViewerSeam(store) {
  const viewer = viewerFor(store);

  return {
    /**
     * A Forge viewer is there: the board's own check before it waits for the model (the model,
     * tree and canvas come after, through `whenModelLoaded`), not the adapter's stricter isReady.
     */
    isReady: () => viewer.readiness().hasViewer,
    /** Run `callback` once the model tree exists (at once when the model already loaded). */
    whenModelLoaded: (callback) => viewer.whenModelLoaded(callback),
    clearCropBox: () => viewer.clearCropBox(),
    /** An AVA Inspect tag's own crop volume, and the camera on it. */
    cropToTag(tag) {
      viewer.loadTagCropVolume(tag);
      viewer.fitBox(tag.cropBox);
    },
    isolateScaffolds: (scaffoldIds) => viewer.isolateScaffolds(scaffoldIds),
    /** World-space points ({ x, y, z }) → viewer-space vectors (worldToView3d makes the vector). */
    worldPointsToViewer: (points) => points.map((point) => viewer.worldToView(point, point)),
    zoomToBox: (box) => viewer.fitBox(box),
    /** The level whose volume holds this world point, or null. */
    levelContaining: (point) => viewer.levelContaining(point) || null,
    zoomToPointIn2d: (point, onDone) => viewer.zoomToPointIn2d(point, onDone),
    highlightTagsIn2d: (tags) => viewer.highlightTagsIn2d(tags),
    /** A world point in viewer space (a new vector; the point itself when there is no model). */
    worldToViewer: (point) => viewer.worldToView(point, point),
    /** The 360 scan nearest a viewer-space point, or null. */
    closestImageTo: (viewerPoint) => viewer.closestImageTo(viewerPoint) || null,
  };
}
